import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Center,
  Code,
  Flex,
  Heading,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import csvToJson from 'csvtojson';
import { Formik, FormikProps, Form, getIn } from 'formik';
import _ from 'lodash';
import React, { useCallback } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';

import { typeguards as commonTypegurads } from '@finley-technologies/common';

import { WipConfig } from '../../types';
import { errorToastAdminConfig } from 'packages/common-frontend/constants';
import { truncateUploadFileName } from 'packages/common-frontend/utils';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FiFilePlus, FiFileMinus } from 'react-icons/fi';

// TODO: @sachinr4 - do not need to handle this, just leaving a note for another csvToJson usage
export const csvArrayFromBuffer = async (text: string): Promise<any[]> => {
  const csvArray = await csvToJson({ noheader: true }).fromString(text);
  return _.map(csvArray, ({ field1 }) => field1);
};

const updateDueDates = async ({
  formProps,
  file,
  basePropertyKey,
  toast,
}: {
  formProps: FormikProps<any>;
  file: FileWithPath & { path: string };
  basePropertyKey: string;
  toast: any;
}) => {
  const text = await file.text();
  const csvArray = await csvArrayFromBuffer(text);
  const [validDates, invalidDates] = _.partition(
    csvArray,
    commonTypegurads.isFullDateStr,
  );
  const uniqueValidDates = _.uniq(validDates);
  if (!_.isEmpty(invalidDates)) {
    errorToastAdminConfig.title = `${invalidDates.length} due date${
      invalidDates.length !== 1 ? 's' : ''
    } ${invalidDates.length !== 1 ? 'are' : 'is'} invalid: ${_.reduce<string>(
      invalidDates,
      (str, val) => str + val,
    )}`;
    errorToastAdminConfig.id = `${basePropertyKey}-invalid-duedate-upload`;
    toast(errorToastAdminConfig);
  } else if (_.isEmpty(validDates)) {
    errorToastAdminConfig.title = 'No valid due dates uploaded';
    errorToastAdminConfig.id = `${basePropertyKey}-invalid-duedate-upload`;
    toast(errorToastAdminConfig);
  } else if (uniqueValidDates.length !== validDates.length) {
    const difference = validDates.length - uniqueValidDates.length;
    errorToastAdminConfig.title = `${difference} duplicate date${
      difference !== 1 ? 's' : ''
    } uploaded`;
    errorToastAdminConfig.id = `${basePropertyKey}-invalid-duedate-upload`;
    toast(errorToastAdminConfig);
  } else {
    formProps.setFieldValue(basePropertyKey, _.sortBy(validDates));
  }
};

type UploadDeliverableDueDateDropzoneProps = {
  isDisabled?: boolean;
  setFieldValue: (value: FileWithPath) => void;
};
const UploadDeliverableDueDateDropzone = ({
  isDisabled,
  setFieldValue,
}: UploadDeliverableDueDateDropzoneProps) => {
  const typesAccepted: string[] = ['.csv'];
  const {
    acceptedFiles,
    fileRejections,
    getRootProps,
    getInputProps,
  } = useDropzone({
    accept: { 'text/*': typesAccepted },
    multiple: false,
    onDrop: useCallback((files: FileWithPath[]) => setFieldValue(files[0]), [
      setFieldValue,
    ]),
    disabled: isDisabled,
  });

  const acceptedFileItems = _.map(acceptedFiles, (file: FileWithPath) => (
    <Text key={file.path} textAlign="center" mt="10px">
      {truncateUploadFileName(file.path)}
    </Text>
  ));

  const fileRejectionItems = _.map(
    fileRejections,
    ({ file }: { file: FileWithPath }) => {
      return (
        <>
          <Text key={file.path} textAlign="center" mt="10px">
            {truncateUploadFileName(file.path)}
          </Text>
          <Text textAlign="center" mt="10px">
            {`Expected file of type `}
            {_.map(typesAccepted, (typeAccepted, index) => (
              <>
                <em key={typeAccepted}>{_.replace(typeAccepted, '.', '')}</em>
                {`${index !== typesAccepted.length - 1 ? ', ' : ' '}`}
              </>
            ))}
          </Text>
        </>
      );
    },
  );

  const fileSelected = !_.isEmpty(acceptedFiles) || !_.isEmpty(fileRejections);

  return (
    <Box width="100%" cursor="pointer">
      <Center
        border="1px"
        borderRadius="md"
        minHeight="200px"
        flexDirection="column"
        {...getRootProps({ className: 'dropzone' })}
      >
        <input {...getInputProps()} />
        {!_.isEmpty(acceptedFiles) && (
          <>
            <Heading size="sm">
              <Icon as={FiFilePlus} color="green.300" /> Accepted
            </Heading>
            {acceptedFileItems}
          </>
        )}
        {!_.isEmpty(fileRejections) && (
          <>
            <Flex alignItems="center">
              <Icon as={FiFileMinus} color="red.300" />
              <Heading size="sm">Rejected</Heading>
            </Flex>
            {fileRejectionItems}
          </>
        )}
        <Text
          textAlign="center"
          as={fileSelected ? 'sub' : 'i'}
          pt={fileSelected ? '20px' : '0'}
        >
          {`Drag a due date csv here, or click to select${
            fileSelected ? ' a new' : ''
          } due date csv`}
        </Text>
      </Center>
    </Box>
  );
};

type AddDeliverableDueDateModalProps = {
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: WipConfig;
};
export const AddDeliverableDueDateModal = ({
  basePropertyKey,
  isOpen,
  setIsOpen,
  wipConfig: { formProps },
}: AddDeliverableDueDateModalProps) => {
  const toast = useToast();
  const currentDeliverable: CreditAgreement.DomainEntity.DeliverablePropertyContainer = getIn(
    formProps.values,
    basePropertyKey,
  );
  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <ModalOverlay />
      <ModalContent padding="20px" minW="2xl">
        <ModalHeader>{`Add Due Dates for ${_.startCase(
          currentDeliverable.deliverableType,
        )}(${currentDeliverable.frequency})`}</ModalHeader>
        <ModalCloseButton />
        <ModalBody width="100%">
          <Alert status="info" mb="10px">
            <AlertIcon />
            <AlertDescription>
              Due dates should be in <Code>FullDateStr</Code> format (eg.
              2020-01-31) stored in a single column csv without headers
            </AlertDescription>
          </Alert>
          <Formik
            onSubmit={({ file }) => {
              if (file) {
                updateDueDates({
                  file,
                  basePropertyKey: `${basePropertyKey}.dueDates`,
                  toast,
                  formProps,
                });
                setIsOpen(false);
              } else {
                errorToastAdminConfig.title =
                  'No valid csv selected as a due date source';
                errorToastAdminConfig.id = `${basePropertyKey}-no-csv-error`;
                toast(errorToastAdminConfig);
              }
            }}
            initialValues={{
              file: undefined,
            }}
          >
            {({ handleSubmit, setFieldValue }) => (
              <Form onSubmit={handleSubmit}>
                <UploadDeliverableDueDateDropzone
                  setFieldValue={(value: FileWithPath) =>
                    setFieldValue('file', value)
                  }
                />
                <Flex justifyContent="center" mt="8">
                  <Button
                    type="submit"
                    aria-label="Done"
                    isDisabled={_.keys(formProps.errors).length > 0}
                  >
                    Done
                  </Button>
                </Flex>
              </Form>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
